import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { isPollStoppedSaga } from "modules/polling/sagas";
import { SagaIterator } from "redux-saga";
import {
  all,
  call,
  put,
  takeEvery,
  takeLatest,
  takeLeading
} from "redux-saga/effects";
import { getAxiosErrorMessage } from "utils/getAxiosErrorMessage";
import { axiosInstance } from "../../axios";
import { clustersActions } from "./slices";
import {
  CreateClusterParams,
  CreateClusterResponse,
  CreateNodegroupParams,
  CreateNodegroupResponse,
  DeleteClusterParams,
  DeleteClusterResponse,
  DeleteNodegroupParams,
  GetClusterParams,
  GetClusterResponse,
  GetClusterSettingsParams,
  GetClusterSettingsResponse,
  GetClusterTemplateParams,
  GetClusterTemplateResponse,
  GetClusterTemplateSettingsParams,
  GetClusterTemplateSettingsResponse,
  GetClusterTemplatesParams,
  GetClusterTemplatesResponse,
  GetClustersParams,
  GetClustersResponse,
  GetNodegroupParams,
  GetNodegroupResponse,
  ListNodegroupsParams,
  ListNodegroupsResponse,
  ResizeClusterParams,
  ResizeClusterResponse,
  RotateClusterCAParams,
  UpdateClusterSettingsParams,
  UpdateClusterSettingsResponse,
  UpdateNodegroupParams,
  UpdateNodegroupResponse,
  UpgradeClusterParams,
  UpgradeClusterResponse
} from "./types";

export function* getClusterSaga(
  action: PayloadAction<GetClusterParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<GetClusterResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-kubernetes/method/${projectId}/clusters/${id}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(clustersActions.getClusterSuccess(response.data));
    }
  } catch (e) {
    yield put(
      clustersActions.getClusterFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get cluster data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getClustersSaga(
  action: PayloadAction<GetClustersParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetClustersResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-kubernetes/method/${projectId}/clusters`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(clustersActions.getClustersSuccess(response.data));
    }
  } catch (e) {
    yield put(
      clustersActions.getClustersFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get clusters data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createClusterSaga(
  action: PayloadAction<CreateClusterParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to create cluster...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, data } = action.payload;
    const response: AxiosResponse<CreateClusterResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-kubernetes/method/${projectId}/clusters`,
      data
    );
    yield put(clustersActions.createClusterSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Request to create cluster has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      clustersActions.createClusterFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to create cluster",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* resizeClusterSaga(
  action: PayloadAction<ResizeClusterParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to resize nodegroup...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id, data } = action.payload;
    const response: AxiosResponse<ResizeClusterResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-kubernetes/method/${projectId}/clusters/${id}/actions/resize`,
      data
    );
    yield put(clustersActions.resizeClusterSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Nodegroup resize initiated successfully.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      clustersActions.resizeClusterFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to resize nodegroup",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* upgradeClusterSaga(
  action: PayloadAction<UpgradeClusterParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Starting cluster upgrade...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id, data } = action.payload;
    const response: AxiosResponse<UpgradeClusterResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-kubernetes/method/${projectId}/clusters/${id}/actions/upgrade`,
      data
    );
    yield put(clustersActions.upgradeClusterSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Cluster upgrade initiated successfully.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      clustersActions.upgradeClusterFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to start cluster upgrade",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteClusterSaga(
  action: PayloadAction<DeleteClusterParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to delete cluster...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<DeleteClusterResponse> = yield call(
      axiosInstance.delete,
      `gotham-${regionId}-kubernetes/method/${projectId}/clusters/${id}`
    );
    yield put(clustersActions.deleteClusterSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Request to delete cluster has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      clustersActions.deleteClusterFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to send request to delete cluster",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getClusterTemplateSaga(
  action: PayloadAction<GetClusterTemplateParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, id } = action.payload;
    const response: AxiosResponse<GetClusterTemplateResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-kubernetes/method/${projectId}/cluster-templates/${id}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(clustersActions.getClusterTemplateSuccess(response.data));
    }
  } catch (e) {
    yield put(
      clustersActions.getClusterTemplateFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get cluster template",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getClusterTemplatesSaga(
  action: PayloadAction<GetClusterTemplatesParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId } = action.payload;
    const response: AxiosResponse<GetClusterTemplatesResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-kubernetes/method/${projectId}/cluster-templates`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(clustersActions.getClusterTemplatesSuccess(response.data));
    }
  } catch (e) {
    yield put(
      clustersActions.getClusterTemplatesFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get cluster templates",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* rotateClusterCASaga(
  action: PayloadAction<RotateClusterCAParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Rotating cluster CA certificate...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, id } = action.payload;
    // API returns 202 Accepted, no response body needed
    yield call(
      axiosInstance.post,
      `gotham-${regionId}-kubernetes/method/${projectId}/clusters/${id}/actions/rotate-ca`
    );
    yield put(clustersActions.rotateClusterCASuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Cluster CA rotation initiated successfully.",
        text: "The process may take some time to complete.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      clustersActions.rotateClusterCAFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to rotate cluster CA certificate",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

// Nodegroup Sagas
export function* listNodegroupsSaga(
  action: PayloadAction<ListNodegroupsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, clusterId } = action.payload;
    const response: AxiosResponse<ListNodegroupsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-kubernetes/method/${projectId}/clusters/${clusterId}/nodegroups`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(clustersActions.listNodegroupsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      clustersActions.listNodegroupsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get nodegroups",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getNodegroupSaga(
  action: PayloadAction<GetNodegroupParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, clusterId, nodegroupId } = action.payload;
    const response: AxiosResponse<GetNodegroupResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-kubernetes/method/${projectId}/clusters/${clusterId}/nodegroups/${nodegroupId}`
    );
    yield put(clustersActions.getNodegroupSuccess(response.data));
  } catch (e) {
    yield put(
      clustersActions.getNodegroupFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get nodegroup details",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createNodegroupSaga(
  action: PayloadAction<CreateNodegroupParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to create nodegroup...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, clusterId, data } = action.payload;
    const response: AxiosResponse<CreateNodegroupResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-kubernetes/method/${projectId}/clusters/${clusterId}/nodegroups`,
      data
    );
    yield put(clustersActions.createNodegroupSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Nodegroup creation initiated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      clustersActions.createNodegroupFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create nodegroup",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateNodegroupSaga(
  action: PayloadAction<UpdateNodegroupParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to update nodegroup autoscaling settings...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, clusterId, nodegroupId, data } =
      action.payload;

    const payloadData: Partial<{
      min_node_count: number;
      max_node_count: number;
    }> = {};
    if (data.min_node_count != null) {
      payloadData.min_node_count = data.min_node_count;
    }
    if (data.max_node_count != null) {
      payloadData.max_node_count = data.max_node_count;
    }

    if (Object.keys(payloadData).length === 0) {
      yield put(
        clustersActions.updateNodegroupSuccess({} as UpdateNodegroupResponse)
      );
      return;
    }

    const response: AxiosResponse<UpdateNodegroupResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-kubernetes/method/${projectId}/clusters/${clusterId}/nodegroups/${nodegroupId}`,
      payloadData
    );

    yield put(clustersActions.updateNodegroupSuccess(response.data));
    yield put(
      clustersActions.listNodegroupsRequest({ regionId, projectId, clusterId })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Nodegroup autoscaling settings update initiated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      clustersActions.updateNodegroupFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update nodegroup autoscaling settings",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteNodegroupSaga(
  action: PayloadAction<DeleteNodegroupParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Sending request to delete nodegroup...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, clusterId, nodegroupId } = action.payload;
    yield call(
      axiosInstance.delete,
      `gotham-${regionId}-kubernetes/method/${projectId}/clusters/${clusterId}/nodegroups/${nodegroupId}`
    );
    yield put(clustersActions.deleteNodegroupSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Nodegroup deletion initiated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      clustersActions.deleteNodegroupFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete nodegroup",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getClusterSettingsSaga(
  action: PayloadAction<GetClusterSettingsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, clusterId } = action.payload;
    const response: AxiosResponse<GetClusterSettingsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-kubernetes/method/${projectId}/clusters/${clusterId}/settings`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(clustersActions.getClusterSettingsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      clustersActions.getClusterSettingsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to load cluster settings",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getClusterTemplateSettingsSaga(
  action: PayloadAction<GetClusterTemplateSettingsParams>
): SagaIterator<void> {
  try {
    const { regionId, projectId, clusterTemplateId } = action.payload;
    const response: AxiosResponse<GetClusterTemplateSettingsResponse> =
      yield call(
        axiosInstance.get,
        `gotham-${regionId}-kubernetes/method/${projectId}/cluster-templates/${clusterTemplateId}/settings`
      );
    yield put(clustersActions.getClusterTemplateSettingsSuccess(response.data));
  } catch (e) {
    yield put(
      clustersActions.getClusterTemplateSettingsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to load cluster template settings",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateClusterSettingsSaga(
  action: PayloadAction<UpdateClusterSettingsParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating cluster settings...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projectId, clusterId, data } = action.payload;
    const response: AxiosResponse<UpdateClusterSettingsResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-kubernetes/method/${projectId}/clusters/${clusterId}/settings`,
      data
    );
    yield put(
      clustersActions.updateClusterSettingsSuccess({
        params: action.payload,
        result: response.data
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Cluster settings update initiated successfully.",
        text: "The process may take up to 30 minutes to complete.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      clustersActions.updateClusterSettingsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update cluster settings",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* clusterTemplatesSaga(): SagaIterator<void> {
  yield all([
    takeEvery(
      clustersActions.getClusterTemplateRequest,
      getClusterTemplateSaga
    ),
    takeEvery(
      clustersActions.getClusterTemplatesRequest,
      getClusterTemplatesSaga
    )
  ]);
}

export function* watcherSaga(): SagaIterator<void> {
  yield all([
    takeLeading(clustersActions.getClusterRequest, getClusterSaga),
    takeLeading(clustersActions.getClustersRequest, getClustersSaga),
    takeEvery(clustersActions.createClusterRequest, createClusterSaga),
    takeEvery(clustersActions.resizeClusterRequest, resizeClusterSaga),
    takeEvery(clustersActions.upgradeClusterRequest, upgradeClusterSaga),
    takeEvery(clustersActions.deleteClusterRequest, deleteClusterSaga),
    takeEvery(clustersActions.rotateClusterCARequest, rotateClusterCASaga),
    takeLatest(
      clustersActions.getClusterTemplateSettingsRequest,
      getClusterTemplateSettingsSaga
    ),
    takeLeading(
      clustersActions.getClusterSettingsRequest,
      getClusterSettingsSaga
    ),
    takeEvery(
      clustersActions.updateClusterSettingsRequest,
      updateClusterSettingsSaga
    ),
    // Nodegroup watchers
    takeLeading(clustersActions.listNodegroupsRequest, listNodegroupsSaga),
    takeLeading(clustersActions.getNodegroupRequest, getNodegroupSaga),
    takeLeading(clustersActions.createNodegroupRequest, createNodegroupSaga),
    takeLeading(clustersActions.updateNodegroupRequest, updateNodegroupSaga),
    takeLeading(clustersActions.deleteNodegroupRequest, deleteNodegroupSaga),
    call(clusterTemplatesSaga)
  ]);
}
